import { ANVIL_ACCOUNTS } from "@gotts.ai/testnet";
import { Option } from "commander";
import { createPublicClient, createWalletClient, http } from "viem";
import { foundry } from "viem/chains";

import type { DeployContext } from "../deployer/deploy-utils";
import type { Address, PublicClient, WalletClient } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScriptOptions {
  /** Anvil RPC URL (default: http://127.0.0.1:8545) */
  rpcUrl?: string;
  /** Account index into ANVIL_ACCOUNTS (default: 0 = deployer) */
  account?: number;
  /** Simulate only, do not send real transactions */
  dryRun?: boolean;
  /** Output structured JSON instead of human-readable text */
  json?: boolean;
}

export interface ScriptContext {
  publicClient: PublicClient;
  walletClient: WalletClient;
  deployer: Address;
  request: (args: { method: string; params: unknown[] }) => Promise<unknown>;
  dryRun: boolean;
  json: boolean;
}

// ---------------------------------------------------------------------------
// Shared Commander Options
// ---------------------------------------------------------------------------

/** Reusable Commander option definitions for script CLIs. */
export const SHARED_OPTIONS = {
  rpcUrl: new Option("--rpc-url <url>", "Anvil RPC URL").default(
    "http://127.0.0.1:8545",
  ),

  account: new Option("--account <index>", "Anvil account index (0-9)")
    .argParser(parseInt)
    .default(0),

  dryRun: new Option(
    "--dry-run",
    "Simulate via eth_call instead of sending transactions",
  ).default(false),

  json: new Option("--json", "Output structured JSON").default(false),
} as const;

// ---------------------------------------------------------------------------
// Context Factory
// ---------------------------------------------------------------------------

/**
 * Create a script execution context from common options.
 *
 * Sets up viem clients pointing at the specified RPC URL and selects the
 * account from Anvil's deterministic HD accounts.
 */
export function createScriptContext(opts: ScriptOptions = {}): ScriptContext {
  const rpcUrl = opts.rpcUrl ?? "http://127.0.0.1:8545";
  const accountIndex = opts.account ?? 0;
  const dryRun = opts.dryRun ?? false;
  const json = opts.json ?? false;

  const account = ANVIL_ACCOUNTS[accountIndex];
  if (!account) {
    throw new Error(`Account index ${String(accountIndex)} out of range (0-9)`);
  }

  const transport = http(rpcUrl);

  const publicClient = createPublicClient({
    chain: foundry,
    transport,
  });

  const walletClient = createWalletClient({
    chain: foundry,
    transport,
  });

  const request = async (args: {
    method: string;
    params: unknown[];
  }): Promise<unknown> => {
    return publicClient.request({
      method: args.method,
      params: args.params,
    } as never);
  };

  return {
    publicClient,
    walletClient,
    deployer: account.address,
    request,
    dryRun,
    json,
  };
}

/**
 * Convert a ScriptContext to a DeployContext.
 *
 * DeployContext is the common type used by all seed/script functions.
 * This adapter drops the extra `dryRun` and `json` fields.
 */
export function toDeployContext(ctx: ScriptContext): DeployContext {
  return {
    publicClient: ctx.publicClient,
    walletClient: ctx.walletClient,
    deployer: ctx.deployer,
    request: ctx.request,
  };
}
